import { createServerFn } from "@tanstack/react-start";
import { count, eq, desc, asc, gte } from "drizzle-orm";
import { ccos, CCOType, coachesCcos } from "~/lib/schemas";

export type CCO = {
  id: string;
  name: string;
  logoUrl: string | null;
  type: CCOType;
  slug: string;
  website: string | null;
  description: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  subcategory: string | null;
  coaches: number; // Aggregate count
};

// Define the structure for the two-level grouping
export type GroupedCCOsByType = {
  [type in CCOType]?: {
    // Optional because a type might have 0 CCOs
    [subcategory: string]: CCO[]; // Use 'Uncategorized' string key directly
  };
};

// Define a constant for the uncategorized key for clarity
export const UNCATEGORIZED_KEY = "Uncategorized";

// Mapping from CCOType to user-facing display name
export const CCO_TYPE_DISPLAY_NAME: Record<CCOType, string> = {
  [CCOType.PAO]: "Professional Accreditation Organizations",
  [CCOType.CTO]: "Coach Training Organizations",
};

// Helper to select base CCO fields (without aggregates)
const selectedCCOFields = {
  id: ccos.id,
  name: ccos.name,
  logoUrl: ccos.logoUrl,
  type: ccos.type,
  slug: ccos.slug,
  website: ccos.website,
  description: ccos.description,
  createdAt: ccos.createdAt,
  updatedAt: ccos.updatedAt,
  subcategory: ccos.subcategory,
};

export const getGroupedCCOs = createServerFn({ method: "GET" })
  .validator((input: unknown) => {
    const all =
      typeof input === "object" && input !== null && "all" in input
        ? Boolean((input as { all: unknown }).all)
        : false;
    return { all };
  })
  .handler(async ({ data }): Promise<GroupedCCOsByType> => {
    const { all } = data;
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const allCCOs = await db
      .select({
        ...selectedCCOFields,
        coaches: count(coachesCcos.coachId),
      })
      .from(ccos)
      .leftJoin(coachesCcos, eq(ccos.id, coachesCcos.ccoId))
      .groupBy(ccos.id)
      .having(gte(count(coachesCcos.coachId), all ? 0 : 1))
      .orderBy(desc(count(coachesCcos.coachId)), asc(ccos.name));

    // Perform the two-level grouping
    const grouped: GroupedCCOsByType = {};

    for (const cco of allCCOs as CCO[]) {
      const type = cco.type;
      const subcategory = cco.subcategory || UNCATEGORIZED_KEY;

      if (!grouped[type]) {
        grouped[type] = {};
      }
      if (!grouped[type]![subcategory]) {
        grouped[type]![subcategory] = [];
      }
      grouped[type]![subcategory].push(cco);
    }

    // Sort subcategories within each type, ensuring 'Uncategorized' is last
    for (const type in grouped) {
      const typeGroup = grouped[type as CCOType]!;
      const sortedSubKeys = Object.keys(typeGroup).sort((a, b) => {
        if (a === UNCATEGORIZED_KEY) return 1;
        if (b === UNCATEGORIZED_KEY) return -1;
        return a.localeCompare(b);
      });

      const sortedSubGroup: { [subcategory: string]: CCO[] } = {};
      for (const subKey of sortedSubKeys) {
        // CCOs are already sorted by coaches count due to initial query ordering
        sortedSubGroup[subKey] = typeGroup[subKey];
      }
      grouped[type as CCOType] = sortedSubGroup;
    }

    return grouped;
  });

export const getCcos = createServerFn({ method: "GET" })
  .validator((input: unknown) => {
    const limit =
      typeof input === "object" && input !== null && "limit" in input
        ? Number((input as { limit: unknown }).limit) || 100
        : 100;
    return { limit };
  })
  .handler(async ({ data }): Promise<{ ccos: CCO[] }> => {
    const { limit } = data;
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const ccosResult = await db
      .select({
        ...selectedCCOFields,
        type: ccos.type,
        coaches: count(coachesCcos.coachId), // Add the coach count
      })
      .from(ccos)
      .leftJoin(coachesCcos, eq(ccos.id, coachesCcos.ccoId))
      .groupBy(ccos.id)
      .orderBy(desc(count(coachesCcos.coachId)), asc(ccos.name))
      .limit(limit);

    // Cast to the defined CCO type
    return {
      ccos: ccosResult as CCO[],
    };
  });

export const getCcoBySlug = createServerFn({ method: "GET" })
  .validator((input: unknown) => {
    if (typeof input !== "object" || input === null || !("slug" in input)) {
      throw new Error("slug is required");
    }
    const slug = String((input as { slug: unknown }).slug);
    return { slug };
  })
  .handler(async ({ data }): Promise<{ cco: CCO | null }> => {
    const { slug } = data;
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();
    const [ccoResult] = await db
      .select({
        ...selectedCCOFields,
        coaches: count(coachesCcos.coachId),
      })
      .from(ccos)
      .where(eq(ccos.slug, slug))
      .leftJoin(coachesCcos, eq(ccos.id, coachesCcos.ccoId))
      .groupBy(ccos.id)
      .limit(1);

    return {
      cco: (ccoResult as CCO) || null,
    };
  });
